import type { ViewportRect } from '@/shared/types/core'

function toDomRect(source: HTMLElement, viewRect: ViewportRect): DOMRect {
  const sourceRect = source.getBoundingClientRect()
  const left = sourceRect.left + viewRect.x
  const top = sourceRect.top + viewRect.y
  const width = viewRect.width
  const height = viewRect.height

  return {
    x: left,
    y: top,
    left,
    top,
    right: left + width,
    bottom: top + height,
    width,
    height,
    toJSON: () => ({}),
  } as DOMRect
}

/**
 * 创建指向真实 DOM 元素的视图级代理。
 *
 * 代理保持事件仍挂载在源元素上，但将尺寸/位置相关查询映射到当前视图矩形，
 * 便于 Three.js 控制器按局部坐标运作。
 *
 * @param source - 真实承载事件的 DOM 元素
 * @param getViewRect - 返回当前视图矩形的回调
 * @returns 行为与 HTMLElement 兼容的代理对象
 */
export function createViewScopedDomElement(
  source: HTMLElement,
  getViewRect: () => ViewportRect
): HTMLElement {
  const proxy = {
    ownerDocument: source.ownerDocument,
    style: source.style,
    addEventListener: source.addEventListener.bind(source),
    removeEventListener: source.removeEventListener.bind(source),
    dispatchEvent: source.dispatchEvent.bind(source),
    focus: source.focus.bind(source),
    blur: source.blur.bind(source),
    click: source.click.bind(source),
    getRootNode: source.getRootNode.bind(source),
    getBoundingClientRect: () => toDomRect(source, getViewRect()),
    setPointerCapture:
      typeof source.setPointerCapture === 'function'
        ? source.setPointerCapture.bind(source)
        : undefined,
    releasePointerCapture:
      typeof source.releasePointerCapture === 'function'
        ? source.releasePointerCapture.bind(source)
        : undefined,
    hasPointerCapture:
      typeof source.hasPointerCapture === 'function'
        ? source.hasPointerCapture.bind(source)
        : undefined,
    hasAttribute: source.hasAttribute.bind(source),
    setAttribute: source.setAttribute.bind(source),
    removeAttribute: source.removeAttribute.bind(source),
    contains: source.contains.bind(source),
  }

  Object.defineProperties(proxy, {
    clientWidth: {
      get: () => getViewRect().width,
    },
    clientHeight: {
      get: () => getViewRect().height,
    },
    offsetWidth: {
      get: () => getViewRect().width,
    },
    offsetHeight: {
      get: () => getViewRect().height,
    },
    offsetLeft: {
      get: () => source.offsetLeft + getViewRect().x,
    },
    offsetTop: {
      get: () => source.offsetTop + getViewRect().y,
    },
    tabIndex: {
      get: () => source.tabIndex,
      set: (value: number) => {
        source.tabIndex = value
      },
    },
  })

  return proxy as unknown as HTMLElement
}
